/**
 * Round director (Phase 40.8): the resolution director (resolutionDirector.ts)
 * applied to a whole round. A round record is final before it plays, so the
 * director can plan the playback up front as a timeline: for every step, how
 * fast to run and which flight the camera follows.
 *
 *   - each live flight carries its own tier speed (viewer-relative, as for a
 *     single shot); the step runs at the slowest live speed;
 *   - a flight that is about to land slows for the approach and the aftermath,
 *     so impacts are never skipped past at fast-forward;
 *   - steps with nothing worth watching in the air run at the dud speed, and a
 *     tail of nothing but shots drifting out of the system is cut short;
 *   - the camera follows the most interesting watchable flight and holds it for
 *     at least a second before switching, then rests on its impact.
 *
 * `summarizeRound` turns the record into the round card the viewer sees after
 * playback — only what they could have known: their own team's shots, hits on
 * their own units, and impacts they could see.
 *
 * Pure; main.ts gathers the viewer-relative inputs and plays the timeline.
 */
import type { ResolutionPace, Vec3 } from '../types';
import type { RoundRecord } from './roundSim';
import { PACE_SPEEDS, type ShotPreview } from './resolutionDirector';
import { PHYS_DT } from '../constants';

export interface RoundFlightInterest {
  flightIndex: number;
  /** The single-shot director's verdict; null at the 'full' pace (everything plays at 1×). */
  preview: ShotPreview | null;
  ownTeam: boolean;
  /** The viewer can see enough of the path to follow it. */
  watchable: boolean;
}

export interface TimelineSegment {
  /** Inclusive step range. */
  fromStep: number;
  toStep: number;
  speed: number;
  /** Flight the camera follows (its current point, or its impact point in the aftermath); null = stay. */
  focus: number | null;
  reason: string;
}

export interface RoundTimeline {
  segments: TimelineSegment[];
  /** Last step to play; a tail of nothing but lost shots drifting away is cut. */
  lastStep: number;
  /** Flights still in the air when the playback stops (announced as lost). */
  cutFlights: number[];
}

export const APPROACH_SECONDS = 1.5;
export const AFTERMATH_SECONDS = 0.75;
/** How long the playback runs on after the last impact, landing or walk before it stops. */
export const TAIL_SECONDS = 1.5;
export const MIN_FOCUS_SECONDS = 1.0;

const steps = (s: number) => Math.round(s / PHYS_DT);

/** The step after which nothing but drifting shots remains. */
export function lastEventStep(record: RoundRecord): number {
  let last = 0;
  for (const i of record.impacts) last = Math.max(last, i.step);
  for (const f of record.flights) if (f.outcome !== 'skipped' && f.outcome !== 'lost') last = Math.max(last, f.endStep);
  for (const a of record.airborne) last = Math.max(last, a.landedStep ?? a.fromStep + a.points.length);
  for (const w of record.walks) last = Math.max(last, w.fromStep + w.dirs.length);
  return last;
}

/** The record's full length in steps. */
export function recordLastStep(record: RoundRecord): number {
  let last = 0;
  for (const f of record.flights) if (f.outcome !== 'skipped') last = Math.max(last, f.endStep);
  for (const a of record.airborne) last = Math.max(last, a.landedStep ?? a.fromStep + a.points.length);
  for (const w of record.walks) last = Math.max(last, w.fromStep + w.dirs.length);
  return last;
}

export function planRoundTimeline(record: RoundRecord, interests: readonly RoundFlightInterest[], pace: ResolutionPace): RoundTimeline {
  const directed = pace !== 'full';
  const speeds = directed ? PACE_SPEEDS[pace] : { highlight: 1, routine: 1, dud: 1 };
  const full = recordLastStep(record);
  const lastEvent = lastEventStep(record);
  const aftermath = steps(AFTERMATH_SECONDS);
  // A record ends the step its last flight lands; the playback rests on that impact before the next turn
  const lastStep = Math.max(directed ? Math.min(full, lastEvent + steps(TAIL_SECONDS)) : full, lastEvent + aftermath);
  const cutFlights = record.flights.filter(f => f.outcome !== 'skipped' && f.endStep > lastStep).map(f => f.index);
  const byIndex = new Map(interests.map(i => [i.flightIndex, i]));
  const approach = steps(APPROACH_SECONDS);

  // Per step: the slowest live interest sets the speed; the most interesting watchable flight is the focus
  const raw: TimelineSegment[] = [];
  for (let s = 0; s <= lastStep; s++) {
    let speed = Infinity;
    let reason = '';
    let focus: number | null = null;
    let focusSpeed = Infinity;
    let focusOwn = false;
    for (const f of record.flights) {
      if (f.outcome === 'skipped' || s < f.launchStep || s > f.endStep + aftermath) continue;
      const it = byIndex.get(f.index);
      const tier = it?.preview?.tier ?? 'highlight';
      const base = it?.preview?.speed ?? 1;
      const landing = f.outcome !== 'lost';
      let v = base;
      let why = it?.preview?.reason ?? 'flight';
      if (s > f.endStep) {
        if (!landing || tier === 'dud') continue;                  // nothing to rest on
        v = tier === 'highlight' ? speeds.highlight : Math.min(base, 2 * speeds.highlight);
        why = 'aftermath';
      } else if (landing && tier !== 'dud' && s >= f.endStep - approach) {
        v = tier === 'highlight' ? speeds.highlight : Math.min(base, 2 * speeds.highlight);
        why = 'impact';
      }
      if (v < speed) { speed = v; reason = why; }
      const watch = !!it && (it.ownTeam || it.watchable);
      if (watch && (v < focusSpeed || (v === focusSpeed && it.ownTeam && !focusOwn))) { focus = f.index; focusSpeed = v; focusOwn = it.ownTeam; }
    }
    for (const w of record.walks) if (s >= w.fromStep && s <= w.fromStep + w.dirs.length && speeds.routine < speed) { speed = speeds.routine; reason = 'movement'; }
    for (const a of record.airborne) if (s >= a.fromStep && s <= (a.landedStep ?? a.fromStep + a.points.length) && speeds.routine < speed) { speed = speeds.routine; reason = a.cause === 'jump' ? 'jump' : 'knockback'; }
    if (speed === Infinity) { speed = speeds.dud; reason = 'nothing in the air'; }
    const prev = raw[raw.length - 1];
    if (prev && prev.speed === speed && prev.focus === focus && prev.reason === reason) prev.toStep = s;
    else raw.push({ fromStep: s, toStep: s, speed, focus, reason });
  }

  // Focus hysteresis: keep following a flight for at least MIN_FOCUS_SECONDS while it is still worth watching
  const minFocus = steps(MIN_FOCUS_SECONDS);
  const stillThere = (idx: number, step: number) => { const f = record.flights[idx]; return !!f && step <= f.endStep + aftermath; };
  let held: number | null = null;
  let heldSince = 0;
  for (const seg of raw) {
    if (seg.focus === held) continue;
    if (held !== null && stillThere(held, seg.fromStep) && seg.fromStep - heldSince < minFocus) { seg.focus = held; continue; }
    held = seg.focus;
    heldSince = seg.fromStep;
  }
  const segments: TimelineSegment[] = [];
  for (const seg of raw) {
    const prev = segments[segments.length - 1];
    if (prev && prev.speed === seg.speed && prev.focus === seg.focus && prev.reason === seg.reason) prev.toStep = seg.toStep;
    else segments.push({ ...seg });
  }
  return { segments, lastStep, cutFlights };
}

/** The segment that covers `step` (segments are contiguous and sorted). */
export function segmentAt(timeline: RoundTimeline, step: number): TimelineSegment | null {
  const segs = timeline.segments;
  let lo = 0, hi = segs.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = segs[mid];
    if (step < s.fromStep) hi = mid - 1;
    else if (step > s.toStep) lo = mid + 1;
    else return s;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Round summary card
// ---------------------------------------------------------------------------

export interface RoundSummaryInput {
  round: number;
  /** Players as they were when the round started (names, teams). */
  players: readonly { id: number; name: string; teamIndex: number }[];
  viewerTeam: number;
  teamName: (teamIndex: number) => string;
  /** Whether the viewer could see this point (fog). */
  canSee: (point: Vec3) => boolean;
}

export interface RoundSummary {
  headline: string;
  lines: string[];
}

/**
 * What the viewer learns from the round: counts of shots, hits and lost shots they
 * could know about, who died, and damage by team. An impact is known when the
 * shooter or a victim is on the viewer's team, or the viewer could see the point.
 */
export function summarizeRound(record: RoundRecord, input: RoundSummaryInput): RoundSummary {
  const team = (id: number) => input.players.find(p => p.id === id)?.teamIndex ?? -1;
  const name = (id: number) => input.players.find(p => p.id === id)?.name ?? 'Unknown';
  const mine = (id: number) => team(id) === input.viewerTeam;
  const impactOf = new Map(record.impacts.map(i => [i.flightIndex, i]));

  let shots = 0, hits = 0, lost = 0;
  const kills: string[] = [];
  const damageByTeam = new Map<number, number>();
  const add = (t: number, d: number) => damageByTeam.set(t, (damageByTeam.get(t) ?? 0) + d);

  const pathSeen = (f: RoundRecord['flights'][number]) => {
    if (f.hitPoint && input.canSee(f.hitPoint)) return true;
    const n = Math.min(8, f.points.length);
    for (let k = 0; k < n; k++) if (input.canSee(f.points[Math.floor(k * (f.points.length - 1) / Math.max(1, n - 1))])) return true;
    return false;
  };
  const knownFlight = (f: RoundRecord['flights'][number]) => {
    if (mine(f.ownerId)) return true;
    const ev = impactOf.get(f.index);
    if (ev && ev.damages.some(d => d.damage > 0 && mine(d.playerId))) return true;
    return pathSeen(f);
  };
  for (const f of record.flights) {
    if (f.outcome === 'skipped' || f.submunitionOf !== null) continue;
    if (!knownFlight(f)) continue;
    shots++;
    if (f.outcome === 'lost') lost++;
  }
  for (const ev of record.impacts) {
    const f = record.flights[ev.flightIndex];
    if (!knownFlight(f) && !input.canSee(ev.point)) continue;
    if (f.submunitionOf === null && (f.hitPlayerId >= 0 || ev.damages.some(d => d.damage > 0))) hits++;
    for (const d of ev.damages) if (d.damage > 0) add(team(d.playerId), d.damage);
    for (const k of ev.kills) kills.push(`${name(k)} (by ${name(f.ownerId)})`);
  }
  for (const a of record.airborne) {
    const last = a.points[a.points.length - 1];
    const known = mine(a.playerId) || (last ? input.canSee(last) : false);
    if (!known) continue;
    if (a.landingDamage > 0) add(team(a.playerId), a.landingDamage);
    if (a.died) kills.push(`${name(a.playerId)} (${a.cause === 'jump' ? 'crash' : 'fall'})`);
  }

  const parts = [`${shots} shot${shots === 1 ? '' : 's'}`, `${hits} hit`];
  if (lost > 0) parts.push(`${lost} lost`);
  const headline = `Round ${input.round}: ${parts.join(', ')}`;
  const lines: string[] = [];
  if (kills.length > 0) lines.push(`Killed: ${kills.join(', ')}`);
  if (damageByTeam.size > 0) {
    const d = [...damageByTeam.entries()].sort((a, b) => a[0] - b[0]).map(([t, dmg]) => `${input.teamName(t)} −${Math.round(dmg)}`);
    lines.push(`Damage: ${d.join(' · ')}`);
  }
  if (kills.length === 0 && damageByTeam.size === 0) lines.push('No damage done');
  return { headline, lines };
}
